// The toast: one short message at a time (a failure, or what a copy did),
// shown in the app's live region (components/ToastRegion.svelte, in
// App.svelte), on whatever page raised it; a page that raises toasts (a
// pull request's, a compare's, a history's) dismisses it when it goes. It
// closes on its own, but never while the pointer or focus is on it (WCAG
// 2.2.1), and a link or an action (Retry) gives the reader more time.

export interface ToastMessage {
  text: string;
  /** A link shown with the text (e.g. the App's installation page). */
  link?: string | null;
  linkText?: string | null;
  /** A button shown with the text (e.g. Retry); using it closes the toast. */
  action?: ToastAction | null;
}

export interface ToastAction {
  label: string;
  run: () => void;
}

export class Toast {
  /** How long a toast stays up (ms): one with a link or an action longer, so it can be reached. */
  static readonly MS = 10000;
  static readonly LINK_MS = 15000;

  /** The message shown, or null when none is. */
  message: ToastMessage | null = $state.raw(null);
  private timer: ReturnType<typeof setTimeout> | null = null;
  private held = false;

  /** Show `m`, replacing any toast shown. It closes after `MS` (`LINK_MS` with a link), not while held. */
  show(m: ToastMessage) {
    this.message = { text: m.text, link: m.link ?? null, linkText: m.link ? (m.linkText ?? null) : null, action: m.action ?? null };
    if (!this.held) this.arm();
  }

  /** Keep the toast up while it is hovered or focused. */
  hold() {
    this.held = true;
    this.clearTimer();
  }

  /** The pointer or focus left the toast: it gets its full time again. */
  release() {
    this.held = false;
    this.arm();
  }

  /** Run the toast's action, once, and close it. */
  act() {
    const action = this.message?.action;
    if (!action) return;
    this.dismiss();
    action.run();
  }

  dismiss() {
    this.clearTimer();
    this.held = false;
    this.message = null;
  }

  private arm() {
    this.clearTimer();
    const shown = this.message;
    if (!shown) return;
    this.timer = setTimeout(
      () => {
        this.timer = null;
        if (this.message === shown) this.dismiss();
      },
      shown.link || shown.action ? Toast.LINK_MS : Toast.MS,
    );
  }

  private clearTimer() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }
}

/** The app's one toast, shown by its one region (one page is on screen at a time). */
export const toast = new Toast();

/**
 * An action for a bar fixed or stuck to the bottom of the screen (the range
 * bar): while the bar's top is on screen, the toast sits above it (the
 * `--toast-bottom` the toast's style reads), so neither covers the other at
 * any width. The lift is kept between the toast's own 18px and the height
 * that keeps the whole toast on screen; with the bar off screen there is
 * none.
 */
export function keepToastAbove(bar: HTMLElement) {
  const root = document.documentElement;
  const gap = 8;
  const least = 18;
  let frame = 0;
  const place = () => {
    frame = 0;
    const top = bar.getBoundingClientRect().top;
    const height = window.innerHeight;
    if (top <= 0 || top >= height) return void root.style.removeProperty('--toast-bottom');
    const toast = document.querySelector('.toast')?.getBoundingClientRect().height ?? 0;
    const lift = Math.min(Math.max(least, Math.round(height - top + gap)), Math.max(least, height - toast));
    root.style.setProperty('--toast-bottom', `${lift}px`);
  };
  const soon = () => (frame ||= requestAnimationFrame(place));
  place();
  const resized = new ResizeObserver(soon);
  resized.observe(bar);
  // A toast shown later, or one whose text changes: its height bounds the lift.
  const shown = new MutationObserver(soon);
  const region = document.querySelector('.toast-region');
  if (region) shown.observe(region, { childList: true, characterData: true, subtree: true });
  // A stuck bar moves when its pane scrolls, and leaves the screen when the window does.
  window.addEventListener('scroll', soon, { capture: true, passive: true });
  window.addEventListener('resize', soon);
  return {
    destroy() {
      cancelAnimationFrame(frame);
      resized.disconnect();
      shown.disconnect();
      window.removeEventListener('scroll', soon, { capture: true });
      window.removeEventListener('resize', soon);
      root.style.removeProperty('--toast-bottom');
    },
  };
}
